import { styled } from "../../../styles";

/**
 * Sample chrome for the tenant theme editor (EN-5154).
 *
 * Two kinds of sample live here, and the difference matters:
 *
 * - Where a **real portal component** shows a token at rest, the preview uses
 *   that component (buttons, the table, the pickers, the slider). It is the
 *   real thing under a scoped tenant theme, so it can't drift.
 * - Where a token only appears on **hover, focus, selection or an open
 *   popup**, a real component shows nothing at rest. Those get a stand-in with
 *   the state pinned, styled from the exact token, and labelled with the state
 *   it represents — otherwise editing that colour changes nothing you can see,
 *   which is what the first cut of this editor did for 81 of the 95 tokens.
 *
 * Every token in the catalogue must appear in one of these two, and
 * `TenantStylePreviews.coverage.test.ts` fails the build if one doesn't.
 */

export const PreviewFrame = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px",
  borderRadius: "6px",
  border: "1px solid $gray-30",
  backgroundColor: "$white",
  fontFamily: "$openSans",
  fontSize: "12px",
});

export const PreviewStack = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "10px",
});

export const PreviewCaption = styled("span", {
  fontSize: "11px",
  color: "$gray-60",
});

/** Groups a set of pinned-state samples under a heading. */
export const PreviewSection = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

export const PreviewSectionLabel = styled("span", {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "$gray-60",
});

export const PreviewRow = styled("div", {
  display: "grid",
  gridTemplateColumns: "minmax(88px, auto) minmax(0, 1fr)",
  alignItems: "center",
  gap: "12px",
});

export const PreviewRowLabel = styled("span", {
  fontSize: "11px",
  color: "$gray-60",
});

/* ---------- navbar ---------- */

export const PreviewNavbar = styled("div", {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px",
  padding: "8px 10px",
  borderRadius: "6px",
  backgroundColor: "$gray-20",
});

export const PreviewNavLink = styled("span", {
  padding: "4px 2px",
  border: "1px solid transparent",
  borderBottom: "2px solid transparent",
  color: "$charcoal",

  variants: {
    state: {
      selected: { borderBottomColor: "$navbar-link-selected-underline" },
      hover: { borderBottomColor: "$navbar-link-hover-underline" },
      focusSelected: {
        borderBottomColor: "$navbar-link-focus-selected-underline",
        borderColor: "$navbar-link-focus-border",
      },
    },
  },
});

export const PreviewNavSearch = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "$white",
  border: "1px solid $navbar-search-bar-active-border",
  color: "$gray-60",
});

export const PreviewNavButton = styled("span", {
  display: "inline-flex",
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "$white",
  border: "1px solid $navbar-button-active-border",
});

export const PreviewWardTrigger = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "$navbar-ward-select-active-background",
  color: "$navbar-ward-select-active-text",

  "& svg, & span[data-icon]": {
    color: "$navbar-ward-select-active-icon",
  },

  variants: {
    state: {
      hover: { backgroundColor: "$navbar-ward-select-active-hover" },
    },
  },
});

export const PreviewWardIcon = styled("span", {
  color: "$navbar-ward-select-active-icon",
  fontWeight: 700,
});

export const PreviewWardOption = styled("span", {
  display: "inline-flex",
  padding: "4px 10px",
  borderRadius: "6px",
  border: "1px solid transparent",
  backgroundColor: "$white",
  color: "$charcoal",

  variants: {
    state: {
      selected: {
        borderColor: "$navbar-ward-select-option-selected-border",
        color: "$navbar-ward-select-option-selected-text",
      },
      hover: {
        backgroundColor: "$navbar-ward-select-option-selected-hover",
        color: "$white",
      },
    },
  },
});

/* ---------- patient table ---------- */

export const PreviewTable = styled("div", {
  display: "flex",
  flexDirection: "column",
  borderRadius: "6px",
  overflow: "hidden",
  border: "1px solid $gray-30",
});

export const PreviewTableRow = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  padding: "7px 10px",
  borderBottom: "1px solid $gray-30",
  border: "1px solid transparent",
  borderBottomColor: "$gray-30",

  "&:last-of-type": { borderBottomColor: "transparent" },

  variants: {
    state: {
      hover: { backgroundColor: "$table-row-hover-background" },
      selected: { backgroundColor: "$table-row-selected-background" },
      focus: {
        backgroundColor: "$table-row-focus-background",
        borderColor: "$table-row-focus-border",
      },
      highlightedPrimary: {
        backgroundColor: "$table-row-highlighted-background-primary",
      },
      highlightedSecondary: {
        backgroundColor: "$table-row-highlighted-background-secondary",
      },
    },
  },
});

export const PreviewMessagesCircle = styled("span", {
  display: "inline-block",
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  backgroundColor: "$table-row-messages-circle",
});

/* ---------- buttons (states the real Button can't show at rest) ---------- */

export const PreviewButtonState = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  padding: "7px 14px",
  borderRadius: "6px",
  fontWeight: 600,
  color: "$button-text",
  backgroundColor: "$button-background",
  border: "1px solid $button-border",

  variants: {
    state: {
      hover: {
        backgroundColor: "$button-hover-background",
        borderColor: "$button-hover-border",
      },
      focus: { borderColor: "$button-focus-border" },
      active: { backgroundColor: "$button-active-background" },
      loadingHover: {
        backgroundColor: "$button-loading-hover-background",
        borderColor: "$button-loading-hover-border",
      },
    },
  },
});

/* ---------- protocol & survey builder ---------- */

export const PreviewBuilderBanner = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "10px",
  borderRadius: "6px",
  backgroundColor: "$builder-banner-background",
});

export const PreviewBuilderTab = styled("span", {
  padding: "4px 2px",
  borderBottom: "2px solid transparent",
  color: "#FFFFFF",

  variants: {
    state: {
      selected: {
        borderBottomColor: "$builder-section-button-selected-underline",
      },
      hover: { borderBottomColor: "$builder-section-button-hover-underline" },
      focus: { borderBottomColor: "$builder-section-button-focus-underline" },
    },
  },
});

export const PreviewBuilderButton = styled("span", {
  display: "inline-flex",
  padding: "6px 12px",
  borderRadius: "6px",
  fontWeight: 600,
  backgroundColor: "$builder-button-primary-background",
  border: "1px solid $builder-button-primary-border",
  color: "$builder-button-primary-text",

  variants: {
    variant: {
      primaryHover: {
        backgroundColor: "$builder-button-primary-hover-background",
      },
      primaryFocus: { borderColor: "$builder-button-primary-focus-border" },
      secondary: {
        backgroundColor: "$builder-button-secondary-background",
        borderColor: "$builder-button-secondary-border",
        color: "$builder-button-secondary-text",
      },
      secondaryHover: {
        backgroundColor: "$builder-button-secondary-hover-background",
        borderColor: "$builder-button-secondary-border",
        color: "$builder-button-secondary-text",
      },
      secondaryFocus: {
        backgroundColor: "$builder-button-secondary-background",
        borderColor: "$builder-button-secondary-focus-border",
        color: "$builder-button-secondary-text",
      },
      tertiary: {
        backgroundColor: "$builder-button-tertiary-background",
        borderColor: "transparent",
        color: "$builder-button-tertiary-text",
      },
      tertiaryHover: {
        backgroundColor: "$builder-button-tertiary-hover-background",
        borderColor: "transparent",
        color: "$builder-button-tertiary-text",
      },
      tertiaryFocus: {
        backgroundColor: "$builder-button-tertiary-background",
        borderColor: "$builder-button-tertiary-focus-border",
        color: "$builder-button-tertiary-text",
      },
    },
  },
});

export const PreviewBuilderAction = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 10px",
  borderRadius: "6px",
  backgroundColor: "$white",
  borderLeft: "3px solid $builder-protocol-action-container-active-bar",
});

/* ---------- measurement modal ---------- */

export const PreviewFilterChip = styled("span", {
  display: "inline-flex",
  padding: "5px 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  color: "$charcoal",

  variants: {
    state: {
      active: {
        backgroundColor: "$measurement-modal-filter-button-active-background",
        borderColor: "$measurement-modal-filter-button-active-border",
        color: "$measurement-modal-filter-button-active-text",
      },
      activeHover: {
        backgroundColor:
          "$measurement-modal-filter-button-active-hover-background",
        borderColor: "$measurement-modal-filter-button-active-border",
        color: "$measurement-modal-filter-button-active-text",
      },
    },
  },
});

export const PreviewGhostButton = styled("span", {
  display: "inline-flex",
  padding: "5px 10px",
  borderRadius: "6px",
  fontWeight: 600,
  backgroundColor: "transparent",

  variants: {
    tone: {
      baselineSave: { color: "$measurement-modal-baseline-save-button-text" },
      confirmCancelHover: {
        color: "$measurement-modal-baseline-confirm-modal-cancel-button-hover",
      },
      careInfo: { color: "$care-info-add-link-button-color" },
      careInfoHover: { color: "$care-info-add-link-button-hover-color" },
      loading: { color: "$portal-loading-state-color" },
    },
  },
});

/* ---------- pickers ---------- */

export const PreviewCell = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "30px",
  padding: "4px 6px",
  borderRadius: "4px",
  border: "1px solid transparent",

  variants: {
    state: {
      today: { borderColor: "$portal-date-picker-popup-today-cell-border" },
      calendarSelected: {
        backgroundColor:
          "$portal-date-picker-popup-calendar-cell-selected-background",
      },
      timeSelected: {
        backgroundColor:
          "$portal-date-picker-popup-time-cell-selected-background",
      },
      timePopupSelected: {
        backgroundColor: "$portal-time-picker-popup-selected-cell",
      },
      rangeToday: {
        backgroundColor: "$portal-date-range-day-today-background",
        color: "$portal-date-range-day-today-text",
      },
      rangeSelected: {
        backgroundColor: "$portal-date-range-day-selected-background",
        color: "$white",
      },
      rangeTrail: { backgroundColor: "$portal-date-range-trail" },
      rangeHover: { borderColor: "$portal-date-range-day-hover-border" },
    },
  },
});

export const PreviewInputBox = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "$white",
  border: "1px solid $gray-40",
  color: "$gray-60",

  variants: {
    state: {
      timeHover: { borderColor: "$portal-time-picker-hover-border" },
      timeFocus: { borderColor: "$portal-time-picker-focus-border" },
      textHover: { borderColor: "$portal-text-input-border-hover" },
      textFocus: { borderColor: "$portal-text-input-border-focus" },
      dropdownSelected: { borderColor: "$portal-dropdown-selected-border" },
    },
  },
});

export const PreviewPopupOption = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "4px 10px",
  borderRadius: "4px",
  backgroundColor: "$portal-dropdown-popup-selected-background",

  "& [data-icon]": { color: "$portal-dropdown-popup-selected-icon" },
});

export const PreviewPopupIcon = styled("span", {
  color: "$portal-dropdown-popup-selected-icon",
  fontWeight: 700,
});

export const PreviewOkButton = styled("span", {
  display: "inline-flex",
  fontWeight: 600,
  color: "$portal-date-picker-popup-ok-button-color",

  variants: {
    state: {
      hover: { color: "$portal-date-picker-popup-ok-button-hover-color" },
    },
  },
});

/* ---------- other portal chrome ---------- */

export const PreviewNote = styled("div", {
  padding: "8px 12px",
  borderRadius: "6px",
  backgroundColor: "$portal-modal-note-background",
  color: "$portal-modal-note-text",
});

export const PreviewToolbar = styled("div", {
  display: "flex",
  gap: "8px",
  padding: "6px 8px",
  borderRadius: "6px",
  border: "1px solid $gray-30",
});

export const PreviewToolbarIcon = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  borderRadius: "4px",
  border: "1px solid transparent",
  fontWeight: 700,

  variants: {
    state: {
      active: {
        backgroundColor: "$portal-rich-text-editor-toolbar-active-background",
        color: "$portal-rich-text-editor-toolbar-active-icon",
      },
      focus: {
        borderColor: "$portal-rich-text-editor-toolbar-focus-border",
        color: "$portal-rich-text-editor-toolbar-focus-icon",
      },
    },
  },
});

export const PreviewEditableField = styled("span", {
  display: "inline-flex",
  padding: "4px 10px",
  borderRadius: "6px",

  variants: {
    state: {
      hover: { backgroundColor: "$patient-details-edit-hover" },
      dropdownHover: {
        backgroundColor: "$patient-details-edit-dropdown-hover",
      },
      addHover: {
        backgroundColor: "$portal-small-add-button-hover",
        color: "$white",
      },
    },
  },
});

/* ---------- editor chrome ---------- */

export const StyleGroup = styled("details", {
  border: "1px solid $gray-30",
  borderRadius: "8px",
  padding: "12px 16px",
  backgroundColor: "$white",

  "& > summary": {
    cursor: "pointer",
    fontFamily: "$openSans",
    fontSize: "14px",
    fontWeight: 600,
    color: "$charcoal",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
});

export const StyleGroupCount = styled("span", {
  fontWeight: 400,
  fontSize: "12px",
  color: "$gray-60",
});

/**
 * Pickers on the left, live sample on the right. The sample gets a real share
 * of the width and grows with the viewport — capped at 380px it was squeezed
 * to a sliver while the token grid sprawled to seven columns (EN-5154 review).
 */
export const StyleGroupBody = styled("div", {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(380px, 0.85fr)",
  gap: "24px",
  paddingTop: "12px",
  alignItems: "start",

  "@media (max-width: 1200px)": {
    gridTemplateColumns: "1fr",
  },
});

/**
 * Keeps the sample in view while scrolling a long group — the builder has 19
 * tokens, so without this the sample it is meant to explain scrolls away.
 */
export const StyleGroupPreview = styled("div", {
  position: "sticky",
  top: "12px",

  "@media (max-width: 1200px)": {
    position: "static",
  },
});

export const StyleTokenGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
  gap: "12px 16px",
  alignContent: "start",
});

/**
 * A div, not a label: the row holds two controls (swatch + hex), and a label
 * wrapping both is invalid and makes each ambiguous to assistive tech. Each
 * input carries its own aria-label instead.
 */
export const StyleTokenRow = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minWidth: 0,
  fontFamily: "$openSans",
  fontSize: "12px",
  color: "$gray-80",
});

/**
 * Token name above its controls, as every other admin field is laid out —
 * beside them it truncated to the point of being unreadable, and these names
 * only differ in their last word (…-hover-background vs …-hover-border).
 */
export const StyleTokenName = styled("span", {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const StyleTokenControls = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

export const StyleSwatch = styled("input", {
  width: "28px",
  height: "24px",
  padding: 0,
  border: "1px solid $gray-40",
  borderRadius: "4px",
  background: "none",
  cursor: "pointer",
  flexShrink: 0,
});

export const StyleHexInput = styled("input", {
  flex: 1,
  minWidth: 0,
  height: "24px",
  padding: "0 6px",
  borderRadius: "4px",
  border: "1px solid $gray-40",
  fontFamily: "monospace",
  fontSize: "12px",
  flexShrink: 0,

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  variants: {
    invalid: {
      true: { borderColor: "$error-dark" },
    },
  },
});
